import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  BILLING_PLAN_IDS,
  type BillingInterval,
  type BillingPlanId,
  type BillingPlanPrice,
} from "@/services/api/billingService"
import { URLS } from "@/utils/urls.const"
import { LOCALES } from "./locales"
import { PLAN_COPY } from "./planCopy"
import { PlanFeatures } from "./PlanFeatures"
import {
  findPrice,
  formatMoney,
  maxYearlySavingsPercent,
  yearlySavings,
} from "./pricing"
import { UpgradeHeading } from "./UpgradeStates"

interface PlanPickerProps {
  plans: readonly BillingPlanPrice[]
  interval: BillingInterval
  onIntervalChange: (interval: BillingInterval) => void
  onSelect: (plan: BillingPlanId) => void
}

// The interval toggle of the settings pages (SettingsControls)
const TOGGLE_ITEM = "flex-none px-4"

export const PlanPicker: React.FC<PlanPickerProps> = ({
  plans,
  interval,
  onIntervalChange,
  onSelect,
}) => {
  const { translate, currentLanguage } = useTranslation()
  const locale = LOCALES[currentLanguage] ?? "en-GB"
  const maxPercent = maxYearlySavingsPercent(plans)

  return (
    <div className="flex flex-col gap-6">
      <UpgradeHeading
        title={translate("UPGRADE/TITLE")}
        description={translate("UPGRADE/SUBTITLE")}
        action={
          <ToggleGroup
            type="single"
            variant="outline"
            value={interval}
            onValueChange={(value) => {
              // Radix reports "" when the active item is clicked again: keep it
              if (value === "month" || value === "year") onIntervalChange(value)
            }}
            aria-label={translate("UPGRADE/INTERVAL_LABEL")}
          >
            <ToggleGroupItem value="month" className={TOGGLE_ITEM}>
              {translate("UPGRADE/INTERVAL_MONTH")}
            </ToggleGroupItem>
            <ToggleGroupItem value="year" className={TOGGLE_ITEM}>
              {maxPercent
                ? translate("UPGRADE/INTERVAL_YEAR_SAVE", {
                    percent: maxPercent,
                  })
                : translate("UPGRADE/INTERVAL_YEAR")}
            </ToggleGroupItem>
          </ToggleGroup>
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        {BILLING_PLAN_IDS.map((plan) => {
          const price = findPrice(plans, plan, interval)
          const savings = yearlySavings(plans, plan)
          const name = translate(PLAN_COPY[plan].name)
          return (
            <Card
              key={plan}
              data-testid={`plan-card-${plan}`}
              className="flex flex-col border-primary"
            >
              <CardHeader className="space-y-0 box-content h-7 flex-row items-center justify-between">
                <CardTitle>{name}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-4">
                {price ? (
                  <div className="flex items-baseline gap-2">
                    <Typography
                      as="h3"
                      className="tabular-nums"
                      data-testid={`plan-price-${plan}`}
                    >
                      {formatMoney(price.unitAmount, price.currency, locale)}
                    </Typography>
                    <Typography as="muted">
                      {translate(
                        interval === "month"
                          ? "UPGRADE/PER_MONTH"
                          : "UPGRADE/PER_YEAR",
                      )}{" "}
                      {translate("UPGRADE/PLUS_VAT")}
                    </Typography>
                  </div>
                ) : null}
                {savings && (
                  <Typography
                    as="small"
                    className="text-muted-foreground"
                    data-testid={`plan-savings-${plan}`}
                  >
                    {interval === "year"
                      ? translate("UPGRADE/SAVE_YEARLY_AMOUNT", {
                          amount: formatMoney(
                            savings.amount,
                            savings.currency,
                            locale,
                          ),
                        })
                      : translate("UPGRADE/SAVE_YEARLY_PERCENT", {
                          percent: String(savings.percent),
                        })}
                  </Typography>
                )}
                <Separator />
                <PlanFeatures plan={plan} />
              </CardContent>
              <CardFooter>
                <Button
                  className="w-full"
                  disabled={!price}
                  onClick={() => onSelect(plan)}
                >
                  {translate("UPGRADE/CHOOSE_PLAN", { plan: name })}
                </Button>
              </CardFooter>
            </Card>
          )
        })}

        {/* Paying by invoice and custom terms stay sales-led: the in-app checkout takes cards only */}
        <Card data-testid="plan-enterprise" className="flex flex-col">
          <CardHeader className="space-y-0 box-content h-7 flex-row items-center justify-between">
            <CardTitle>{translate("UPGRADE/ENTERPRISE_TITLE")}</CardTitle>
          </CardHeader>
          <CardContent className="flex-1">
            <Typography as="muted">
              {translate("UPGRADE/ENTERPRISE_DESCRIPTION")}
            </Typography>
          </CardContent>
          <CardFooter>
            <Button variant="outline" className="w-full" asChild>
              <a href={URLS.ENTERPRISE_CONTACT}>
                {translate("UPGRADE/ENTERPRISE_CONTACT")}
              </a>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
